import { OfferingCategory } from "@albertine/types";
import type { Booking as BookingDto } from "@albertine/types/openapi/lmt";
import { type Booking, ProposalFeedbackEnum } from "../common/types/Booking";
import { omitUndefined } from "./object.util";
import { isoToFirebaseTimestamp } from "./timestamp.util";

function toOfferingCategory(
    value: string | null | undefined,
): OfferingCategory | null | undefined {
    if (value == null) {
        return value;
    }
    const parsed = OfferingCategory.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}

/** Map API booking DTO → LMT Booking. Omits nullish fields (Firebase callable encode turns `undefined` into `null`). */
export function mapApiBookingToBooking(b: BookingDto): Booking {
    const proposalFeedback = ProposalFeedbackEnum.safeParse(b.proposal?.feedback);

    return omitUndefined({
        id: b.id,
        request: b.requestId,
        member: b.memberId,
        createdBy: b.createdByAgentId,
        catalogItem: b.catalogItemId,
        archived: b.archived,
        title: b.title,
        description: b.description ?? undefined,
        website: b.website ?? undefined,
        phoneNumber: b.phoneNumber ?? undefined,
        address: b.address ?? undefined,
        country: b.country ?? undefined,
        googleUrl: b.googleUrl ?? undefined,
        geoPoint: b.geoPoint ?? undefined,
        offeringCategory: toOfferingCategory(b.offeringCategory),
        numberOfGuests: b.numberOfGuests ?? undefined,
        numberOfNights: b.numberOfNights ?? undefined,
        rooms: b.rooms ?? undefined,
        price:
            b.price?.amount != null && b.price.currency != null
                ? { amount: b.price.amount, currency: b.price.currency }
                : undefined,
        priceCurrency: b.price?.currency ?? undefined,
        priceQuote: b.price?.quote ?? undefined,
        paymentTo: b.price?.paymentTo ?? undefined,
        priceDetails: b.price?.details ?? undefined,
        imageURL: b.imageUrl ?? undefined,
        croppedImageURL: b.croppedImageUrl ?? undefined,
        absoluteCrop: b.absoluteCrop ?? undefined,
        relativeCrop: b.relativeCrop ?? undefined,
        images: b.images ?? undefined,
        attachments: b.attachments ?? undefined,
        options: b.options ?? undefined,
        reference: b.reference ?? undefined,
        reservationStatus: b.reservationStatus,
        notesForMember: b.notes?.text ?? undefined,
        notesForMemberSenderId: b.notes?.senderId ?? undefined,
        notesForMemberSenderName: b.notes?.senderName ?? undefined,
        suppliers: b.suppliers?.ids ?? undefined,
        supplierDetails: b.suppliers?.details ?? undefined,
        startTime: isoToFirebaseTimestamp(b.startTime),
        startTimezone: b.startTimezone ?? undefined,
        endTime: isoToFirebaseTimestamp(b.endTime),
        endTimezone: b.endTimezone ?? undefined,
        reviewNeeded: b.reviewNeeded ?? undefined,
        headline: b.headline ?? undefined,
        benefits: b.benefits ?? undefined,
        bookingPolicy: b.bookingPolicy ?? undefined,
        calendarEventId: b.calendarEventId,
        proposalFeedback: proposalFeedback.success ? proposalFeedback.data : undefined,
        proposalFeedbackTimestamp: isoToFirebaseTimestamp(
            b.proposal?.feedbackTimestamp,
        ),
        bookingRating: b.booking?.rating ?? undefined,
        bookingFeedback: b.booking?.feedback ?? undefined,
        bookingFeedbackTimestamp: isoToFirebaseTimestamp(b.booking?.feedbackTimestamp),
        createdAt: isoToFirebaseTimestamp(b.createdAt),
        updatedAt: isoToFirebaseTimestamp(b.updatedAt),
    });
}
